import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Checkbox,
  ListItemText,
} from '@mui/material'
import { Add as AddIcon, Send as SendIcon } from '@mui/icons-material'
import { ticketsAPI } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import LoadingSpinner from './LoadingSpinner'
import FileUpload from './FileUpload'

const steps = ['Select Agent Type', 'Configure Data', 'Review & Create']

const CreateTicketDialog = ({ open, onClose, onTicketCreated }) => {
  // All hooks must be called at the top level in the same order every time
  const { user } = useAuth()
  const navigate = useNavigate()
  
  // All useState hooks
  const [activeStep, setActiveStep] = useState(0)
  const [ticketType, setTicketType] = useState('')
  const [data, setData] = useState({})
  const [initialMessage, setInitialMessage] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  const [ticketTypesData, setTicketTypesData] = useState(null)
  const [typesLoading, setTypesLoading] = useState(false)
  const [typesError, setTypesError] = useState(null)
  const [schema, setSchema] = useState(null)
  const [schemaLoading, setSchemaLoading] = useState(false)
  const [schemaError, setSchemaError] = useState(null)

  // All useEffect hooks grouped together
  // Load ticket types when dialog opens
  useEffect(() => {
    if (open && !ticketTypesData) {
      loadTicketTypes()
    }
  }, [open, ticketTypesData]) // loadTicketTypes is stable, no need to include

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setActiveStep(0)
      setTicketType('')
      setData({})
      setInitialMessage('')
      setError('')
      setSchema(null)
      setSchemaError(null)
    }
  }, [open])

  // Load ticket types function
  const loadTicketTypes = async () => {
    try {
      setTypesLoading(true)
      setTypesError(null)
      const data = await ticketsAPI.getTicketTypes()
      setTicketTypesData(data)
    } catch (error) {
      setTypesError(error)
    } finally {
      setTypesLoading(false)
    }
  }
  
  // Load ticket schema function
  const loadTicketSchema = async (ticketType) => {
    try {
      setSchemaLoading(true)
      setSchemaError(null)
      const schemaData = await ticketsAPI.getTicketSchema(ticketType)
      setSchema(schemaData)
    } catch (error) {
      setSchemaError(error)
      console.error(`Failed to load schema for ${ticketType}:`, error)
    } finally {
      setSchemaLoading(false)
    }
  }

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  const handleTicketTypeChange = async (type) => {
    setTicketType(type)
    setData({}) // Reset data when type changes
    
    // Load schema for the selected ticket type
    if (type) {
      await loadTicketSchema(type)
    } else {
      setSchema(null)
      setSchemaError(null)
    }
  }

  const handleDataChange = (field, value) => {
    setData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const validateStep = (step) => {
    switch (step) {
      case 0:
        return !!ticketType
      case 1:
        // Validate required fields based on dynamic schema
        if (schema?.schema) {
          const requiredFields = schema.schema.filter(field => field.mandatory)
          return requiredFields.every(field => {
            const value = data[field.name]
            // Check if field has value and meets min_length requirement if specified
            if (!value || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0)) {
              return false
            }
            if (field.min_length && value.length < field.min_length) {
              return false
            }
            return true
          })
        }
        return true
      case 2:
        return true
      default:
        return false
    }
  }

  const handleCreate = async () => {
    setCreating(true)
    setError('')

    try {
      // Validate user is loaded
      if (!user || !user.email) {
        setError('User information not loaded. Please refresh the page and try again.')
        setCreating(false)
        return
      }

      const newTicket = await ticketsAPI.createTicket({
        ticket_type: ticketType,
        assigned_to: user.email,
        data,
        initial_message: initialMessage || undefined,
      })

      console.log(`Ticket ${newTicket.id} has just been created..... navigating to /tickets/${newTicket.id}`)
      
      // Close dialog after successful creation
      onClose()
      
      // Navigate directly to the new ticket
      navigate(`/tickets/${newTicket.id}`)
      
      // Notify parent component with the new ticket
      onTicketCreated?.(newTicket)
    } catch (error) {
      // Handle different error response formats
      let errorMessage = 'Failed to create ticket'
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // FastAPI validation errors
          const details = error.response.data.detail.map(err => err.msg).join(', ')
          errorMessage = `Validation error: ${details}`
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      setError(errorMessage)
    } finally {
      setCreating(false)
    }
  }
  
  // Helper function to render form fields dynamically based on schema
  const renderSchemaFields = () => {
    if (schemaLoading) {
      return <LoadingSpinner message="Loading field configuration..." />
    }
    
    if (schemaError) {
      return (
        <Alert severity="error">
          Failed to load field configuration: {schemaError.message || 'Unknown error'}
        </Alert>
      )
    }
    
    if (!schema?.schema || schema.schema.length === 0) {
      return (
        <Paper
          sx={{
            p: 3,
            bgcolor: 'background.default',
            borderLeft: 4,
            borderLeftColor: 'primary.main',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            ✨ The {ticketType} agent doesn't require any additional data.
            You can provide an initial message in the next step.
          </Typography>
        </Paper>
      )
    }
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {schema.schema.map((field) => {
          const fieldValue = data[field.name] || ''
          
          // Handle different field types
          if (field.type === 'file_upload') {
            // Render as file upload component
            const fileTypes = field.file_types ? field.file_types.split(',').map(t => t.trim()) : []
            const fileTypeLabel = fileTypes.length > 0 ? `(${fileTypes.join(', ').toUpperCase()})` : ''

            return (
              <Box key={field.name}>
                <Typography variant="subtitle2" gutterBottom>
                  {field.name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {field.mandatory && ' *'}
                </Typography>
                <FileUpload
                  onFileUploaded={(filePath, fileInfo) => {
                    handleDataChange(field.name, filePath)
                  }}
                  onFileRemoved={() => {
                    handleDataChange(field.name, '')
                  }}
                  currentValue={fieldValue}
                  required={field.mandatory}
                  label={`Upload File ${fileTypeLabel}`}
                  helperText={field.description || `Select a file from your computer ${fileTypeLabel}`}
                  acceptedFileTypes={fileTypes}
                  agentType={ticketType}
                  ticketId={null} // No ticket ID yet during creation
                />
              </Box>
            )
          } else if (field.type === 'multi_select' && field.choices && field.choices.length > 0) {
            // Render as multi-select with {value, label} choices
            const multiValue = data[field.name] || []
            const label = (field.label ?? field.name).replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
            return (
              <FormControl key={field.name} fullWidth required={field.mandatory}>
                <InputLabel>{label}</InputLabel>
                <Select
                  multiple
                  value={multiValue}
                  label={label}
                  onChange={(e) => handleDataChange(field.name, e.target.value)}
                  renderValue={(selected) => `${selected.length} invoice${selected.length !== 1 ? 's' : ''} selected`}
                >
                  {field.choices.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      <Checkbox checked={multiValue.indexOf(choice.value) > -1} />
                      <ListItemText primary={choice.label} />
                    </MenuItem>
                  ))}
                </Select>
                {field.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {field.description}
                  </Typography>
                )}
              </FormControl>
            )
          } else if (field.type === 'select' && field.choices && field.choices.length > 0) {
            // Render as select with {id, label} choices
            return (
              <FormControl key={field.name} fullWidth required={field.mandatory}>
                <InputLabel>{(field.label ?? field.name).replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</InputLabel>
                <Select
                  value={fieldValue}
                  label={(field.label ?? field.name).replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  onChange={(e) => handleDataChange(field.name, e.target.value)}
                >
                  {field.choices.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </Select>
                {field.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {field.description}
                  </Typography>
                )}
              </FormControl>
            )
          } else if (field.choices && field.choices.length > 0) {
            // Render as dropdown/select
            return (
              <FormControl key={field.name} fullWidth required={field.mandatory}>
                <InputLabel>{field.name.replace('_', ' ')}</InputLabel>
                <Select
                  value={fieldValue}
                  label={field.name.replace('_', ' ')}
                  onChange={(e) => handleDataChange(field.name, e.target.value)}
                >
                  {field.choices.map((choice) => (
                    <MenuItem key={choice} value={choice}>
                      {choice}
                    </MenuItem>
                  ))}
                </Select>
                {field.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {field.description}
                  </Typography>
                )}
              </FormControl>
            )
          } else {
            // Render as text field
            return (
              <TextField
                key={field.name}
                fullWidth
                required={field.mandatory}
                label={field.name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                value={fieldValue}
                onChange={(e) => handleDataChange(field.name, e.target.value)}
                placeholder={field.default_value || ''}
                helperText={field.description}
                inputProps={{
                  minLength: field.min_length,
                  maxLength: field.max_length,
                  ...(field.type === 'number' && {
                    type: 'number',
                    min: field.min_value,
                    max: field.max_value,
                  }),
                  ...(field.validation_regex && {
                    pattern: field.validation_regex,
                  }),
                }}
                type={field.type === 'number' ? 'number' : 'text'}
              />
            )
          }
        })}
      </Box>
    )
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Agent Type
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose the type of agent that will process this ticket
            </Typography>

            {typesLoading ? (
              <LoadingSpinner message="Loading agent types..." />
            ) : typesError ? (
              <Alert severity="error">
                {typeof typesError === 'string' ? typesError : 'Failed to load agent types'}
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Agent Type</InputLabel>
                <Select
                  value={ticketType}
                  label="Agent Type"
                  onChange={(e) => handleTicketTypeChange(e.target.value)}
                >
                  {ticketTypesData?.ticket_types?.map((type) => (
                    <MenuItem key={type.name} value={type.name}>
                      <Box>
                        <Typography variant="body1">{type.name}</Typography>
                        {type.description && (
                          <Typography variant="caption" color="text.secondary">
                            {type.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configure Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Provide the required information for the {ticketType} agent
            </Typography>

            {renderSchemaFields()}
          </Box>
        )

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Create
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review your ticket configuration and add an optional initial message
            </Typography>

            {/* Review Section */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom>
                Ticket Configuration
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Agent Type:</strong> {ticketType}
                </Typography>
                <Typography variant="body2">
                  <strong>Assigned To:</strong> {user?.email || 'Loading...'}
                </Typography>
                {Object.keys(data).length > 0 && (
                  <Box>
                    <Typography variant="body2">
                      <strong>Data:</strong>
                    </Typography>
                    <Box sx={{ ml: 2 }}>
                      {Object.entries(data).map(([key, value]) => (
                        <Typography key={key} variant="body2">
                          • {key}: {Array.isArray(value) ? `[${value.join(', ')}]` : value}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Initial Message */}
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              label="Initial Message (Optional)"
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
              placeholder="Add any additional context or instructions..."
              helperText="This message will be included when the ticket is created"
            />

            {!user && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                User information is loading. Please wait...
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {typeof error === 'string' ? error : 'An error occurred while creating the ticket'}
              </Alert>
            )}
          </Box>
        )

      default:
        return null
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon />
          Create New Ticket
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        {activeStep > 0 && (
          <Button onClick={handleBack}>
            Back
          </Button>
        )}
        
        <Box sx={{ flex: 1 }} />
        
        <Button onClick={onClose}>
          Cancel
        </Button>
        
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!validateStep(activeStep)}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleCreate}
            disabled={creating || !validateStep(activeStep) || !user || !user.email}
          >
            {creating ? 'Creating...' : 'Create Ticket'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default CreateTicketDialog